import * as d3 from 'd3'
import { useRef } from 'react'
import { useD3, type D3Svg, type D3Dims } from './useD3.js'
import { CHART_PALETTE } from './chartTokens.js'
import { useChartTooltip, type TooltipVariant } from './useChartTooltip.js'
import { ChartLegend } from './ChartLegend.js'
import styles from './BarChart.module.css'

export interface BarDatum {
  label: string
  value: number
  color?: string
}

interface BarChartProps {
  data: BarDatum[]
  direction?: 'vertical' | 'horizontal'
  showTooltip?: boolean
  formatTooltip?: (datum: BarDatum) => string
  showLegend?: boolean
  tooltipVariant?: TooltipVariant
  className?: string
  /** 막대 클릭 핸들러. hit area에 바인딩되어 값이 0인 막대도 클릭 가능. */
  onBarClick?: (datum: BarDatum) => void
}

interface TooltipCtx {
  show: (x: number, y: number, text: string) => void
  hide: () => void
  format?: (datum: BarDatum) => string
}

type BarClickHandler = (datum: BarDatum) => void

const MARGIN = { top: 16, right: 16, bottom: 32, left: 48 }

function renderVertical(svg: D3Svg, dims: D3Dims, data: BarDatum[], tt?: TooltipCtx, onBarClick?: BarClickHandler) {
  const w = dims.width - MARGIN.left - MARGIN.right
  const h = dims.height - MARGIN.top - MARGIN.bottom
  if (w <= 0 || h <= 0) return

  const g = svg.append('g').attr('transform', `translate(${MARGIN.left},${MARGIN.top})`)

  const x = d3.scaleBand()
    .domain(data.map((d) => d.label))
    .range([0, w])
    .padding(0.3)

  const y = d3.scaleLinear()
    .domain([0, d3.max(data, (d) => d.value) ?? 0])
    .nice()
    .range([h, 0])

  // 라벨 자동 thinning — 라벨 1개당 ~52px 잡고 넘치면 stride로 건너뜀
  const maxLabels = Math.max(1, Math.floor(w / 52))
  const stride = Math.max(1, Math.ceil(data.length / maxLabels))
  const tickValues = data
    .filter((_, i) => i % stride === 0)
    .map((d) => d.label)

  g.append('g')
    .attr('transform', `translate(0,${h})`)
    .call(d3.axisBottom(x).tickValues(tickValues).tickSize(0).tickPadding(8))
    .call((el) => el.select('.domain').attr('stroke', '#E8E4DF'))
    .selectAll('text')
    .attr('fill', '#9E9996')
    .attr('font-size', '11px')

  g.append('g')
    .call(d3.axisLeft(y).ticks(5).tickSize(-w).tickPadding(8))
    .call((el) => el.select('.domain').remove())
    .call((el) => el.selectAll('.tick line').attr('stroke', '#E8E4DF').attr('stroke-dasharray', '2,2'))
    .selectAll('text')
    .attr('fill', '#9E9996')
    .attr('font-size', '11px')

  g.selectAll('.bar')
    .data(data)
    .enter()
    .append('rect')
    .attr('x', (d) => x(d.label)!)
    .attr('y', (d) => y(d.value))
    .attr('width', x.bandwidth())
    .attr('height', (d) => h - y(d.value))
    .attr('fill', (d, i) => d.color ?? CHART_PALETTE[i % CHART_PALETTE.length])
    .attr('rx', 4)

  if (tt || onBarClick) {
    // 투명 hit area — 각 데이터 포인트마다 컬럼 전체 높이를 덮어서
    // 값=0(높이 0)인 막대도 hover/click 가능. step()으로 padding 영역까지 흡수.
    const step = x.step()
    const hit = g.selectAll('.hit')
      .data(data)
      .enter()
      .append('rect')
      .attr('x', (_, i) => i * step)
      .attr('y', 0)
      .attr('width', step)
      .attr('height', h)
      .attr('fill', 'transparent')
      .style('cursor', 'pointer')

    if (tt) {
      hit
        .on('mouseover mousemove', (event, d) => {
          const text = tt.format?.(d) ?? `${d.label}: ${d.value}`
          tt.show(event.clientX + 12, event.clientY - 10, text)
        })
        .on('mouseout', () => tt.hide())
    }
    if (onBarClick) {
      hit.on('click', (_event, d) => onBarClick(d))
    }
  }
}

function renderHorizontal(svg: D3Svg, dims: D3Dims, data: BarDatum[], tt?: TooltipCtx, onBarClick?: BarClickHandler) {
  const margin = { top: 16, right: 16, bottom: 16, left: 80 }
  const w = dims.width - margin.left - margin.right
  const h = dims.height - margin.top - margin.bottom
  if (w <= 0 || h <= 0) return

  const g = svg.append('g').attr('transform', `translate(${margin.left},${margin.top})`)

  const yScale = d3.scaleBand()
    .domain(data.map((d) => d.label))
    .range([0, h])
    .padding(0.3)

  const xScale = d3.scaleLinear()
    .domain([0, d3.max(data, (d) => d.value) ?? 0])
    .nice()
    .range([0, w])

  g.append('g')
    .call(d3.axisLeft(yScale).tickSize(0).tickPadding(8))
    .call((el) => el.select('.domain').remove())
    .selectAll('text')
    .attr('fill', '#9E9996')
    .attr('font-size', '11px')

  g.append('g')
    .attr('transform', `translate(0,${h})`)
    .call(d3.axisBottom(xScale).ticks(5).tickSize(-h).tickPadding(8))
    .call((el) => el.select('.domain').remove())
    .call((el) => el.selectAll('.tick line').attr('stroke', '#E8E4DF').attr('stroke-dasharray', '2,2'))
    .selectAll('text')
    .attr('fill', '#9E9996')
    .attr('font-size', '11px')

  g.selectAll('.bar')
    .data(data)
    .enter()
    .append('rect')
    .attr('x', 0)
    .attr('y', (d) => yScale(d.label)!)
    .attr('width', (d) => xScale(d.value))
    .attr('height', yScale.bandwidth())
    .attr('fill', (d, i) => d.color ?? CHART_PALETTE[i % CHART_PALETTE.length])
    .attr('rx', 4)

  if (tt || onBarClick) {
    // 투명 hit area — 값=0(너비 0)인 막대도 hover/click 가능하도록 행 전체 폭을 덮음
    const step = yScale.step()
    const hit = g.selectAll('.hit')
      .data(data)
      .enter()
      .append('rect')
      .attr('x', 0)
      .attr('y', (_, i) => i * step)
      .attr('width', w)
      .attr('height', step)
      .attr('fill', 'transparent')
      .style('cursor', 'pointer')

    if (tt) {
      hit
        .on('mouseover mousemove', (event, d) => {
          const text = tt.format?.(d) ?? `${d.label}: ${d.value}`
          tt.show(event.clientX + 12, event.clientY - 10, text)
        })
        .on('mouseout', () => tt.hide())
    }
    if (onBarClick) {
      hit.on('click', (_event, d) => onBarClick(d))
    }
  }
}

export function BarChart({
  data,
  direction = 'vertical',
  showTooltip: enableTooltip = false,
  formatTooltip,
  showLegend = false,
  tooltipVariant,
  className,
  onBarClick,
}: BarChartProps) {
  const { tooltipElement, show, hide } = useChartTooltip(tooltipVariant)
  const formatRef = useRef(formatTooltip)
  formatRef.current = formatTooltip
  const clickRef = useRef(onBarClick)
  clickRef.current = onBarClick

  const ref = useD3(
    (svg, dims) => {
      const tt = enableTooltip ? { show, hide, format: formatRef.current } : undefined
      const click = clickRef.current ? (d: BarDatum) => clickRef.current?.(d) : undefined
      if (direction === 'horizontal') renderHorizontal(svg, dims, data, tt, click)
      else renderVertical(svg, dims, data, tt, click)
    },
    [data, direction, enableTooltip]
  )

  const legendItems = showLegend
    ? data.map((d, i) => ({
        label: d.label,
        color: d.color ?? CHART_PALETTE[i % CHART_PALETTE.length],
      }))
    : undefined

  return (
    <div className={`${styles.root}${className ? ` ${className}` : ''}`}>
      <svg ref={ref} className={styles.svg} />
      {enableTooltip && tooltipElement}
      {legendItems && <ChartLegend items={legendItems} />}
    </div>
  )
}
